<template>
  <div>
    <div class="tw-space-y-2">
      <div
        v-for="(tier, index) in tiers"
        :key="tier.tierId"
        :class="rowClass"
      >
        <o-field
          label="Up to $"
          label-size="small"
          style="margin: 0; flex-grow: 0;"
          label-class="!tw-font-normal"
        >
          <o-input
            v-if="index < tiers.length - 1"
            :value="formatDollar(tier.upTo)"
            type="text"
            inputmode="numeric"
            placeholder="0"
            size="small"
            :root-class="upToWidthClass"
            @input="val => writeUpTo(tier, val)"
          />
          <span
            v-else
            :class="['tw-text-sm tw-text-gray-600 tw-inline-block tw-mt-1', upToWidthClass]"
          >∞ (unlimited)</span>
        </o-field>
        <o-field
          label="Rate (%)"
          label-size="small"
          style="margin: 0; flex-grow: 0;"
          label-class="!tw-font-normal"
        >
          <o-input
            v-model.number="tier.rate"
            type="number"
            step="0.001"
            placeholder="0.000"
            size="small"
            :root-class="rateWidthClass"
          />
        </o-field>
        <button
          v-if="canRemove(index)"
          class="tw-text-red-500 hover:tw-text-red-700 tw-bg-transparent
            tw-border-none tw-cursor-pointer tw-ml-auto tw-mt-3"
          @click="removeTier(index)"
        >
          ✕
        </button>
      </div>
    </div>
    <button
      class="tw-mt-2 tw-text-xs tw-text-purple-600 hover:tw-text-purple-800 tw-cursor-pointer tw-bg-transparent tw-border-none"
      @click="addTier"
    >+ Add Tier</button>
    <ul
      v-if="errors && errors.length"
      class="tw-mt-2 tw-text-xs tw-text-red-500 tw-list-disc tw-pl-5"
    >
      <li v-for="(err, i) in errors" :key="i">{{ err }}</li>
    </ul>
  </div>
</template>

<script>
import { withTierId } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';

export default {
  name: 'FeeScheduleTierList',
  props: {
    // The parent owns the array; we splice it in place so reactivity bubbles
    // up. Each tier must have a stable `tierId` (use `withTierId`).
    tiers: { type: Array, required: true },
    errors: { type: Array, default: () => [] },
    compact: { type: Boolean, default: true },
  },
  computed: {
    rowClass() {
      return [
        'tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 tw-rounded-lg',
        this.compact ? 'tw-p-2' : 'tw-p-3 tw-gap-4',
      ];
    },
    upToWidthClass() {
      // Wide enough to comfortably show "100,000,000" with the small font.
      return this.compact ? 'tw-w-28' : 'tw-w-40';
    },
    rateWidthClass() {
      return this.compact ? 'tw-w-16' : 'tw-w-20';
    },
  },
  methods: {
    canRemove(index) {
      // Don't allow removing the implicit "∞" (last) row.
      return this.tiers.length > 1 && index < this.tiers.length - 1;
    },
    addTier() {
      // Insert before the implicit "∞" (last) row. We mutate the prop array
      // in place; Vue's array reactivity propagates the change to the parent.
      // eslint-disable-next-line vue/no-mutating-props
      this.tiers.splice(this.tiers.length - 1, 0, withTierId({ upTo: 0, rate: 0 }));
    },
    removeTier(index) {
      if (!this.canRemove(index)) return;
      // eslint-disable-next-line vue/no-mutating-props
      this.tiers.splice(index, 1);
      if (this.tiers.length === 0) {
        // eslint-disable-next-line vue/no-mutating-props
        this.tiers.push(withTierId({ upTo: null, rate: 0 }));
      }
    },
    formatDollar(value) {
      if (value == null || value === '') return '';
      const num = Number(value);
      if (!Number.isFinite(num)) return '';
      return num.toLocaleString('en-US');
    },
    writeUpTo(tier, value) {
      if (value === '' || value == null) {
        tier.upTo = null;
        return;
      }
      // Strip anything that isn't a digit, dot, or sign. Comma stripping
      // means "1,000,000" parses to 1000000 cleanly.
      const cleaned = String(value).replace(/[^0-9.-]/g, '');
      if (cleaned === '' || cleaned === '-' || cleaned === '.') {
        tier.upTo = null;
        return;
      }
      const num = Number(cleaned);
      tier.upTo = Number.isFinite(num) ? num : null;
    },
  },
};
</script>
